/**
 * src/components/spatial/ChatMessageBody.tsx
 *
 * One chat message, shown as the thing it is rather than as a wall of text.
 *
 * The parsing and the safety rules live in `chatRichText`; this is only the
 * mapping from segments to elements. There is no `dangerouslySetInnerHTML`
 * anywhere in the path — a message cannot introduce markup regardless of
 * what it contains, because nothing here interprets a string as HTML.
 *
 * Four behaviours are worth knowing about, and each exists because the
 * obvious alternative made an answer worse than the plain text it replaced.
 *
 * AN IMAGE THAT FAILS TO LOAD BECOMES A LINK. The URL a model produces may
 * point nowhere — the case that prompted this pointed at `example.com`,
 * which by standard can never serve one — and a broken-image icon is less
 * useful than the words and the address were.
 *
 * AN IDENTIFIER IS TRUNCATED ON SCREEN AND WHOLE IN THE DOM. A contract
 * address is fifty-six characters, and a 320-pixel card laying one out as a
 * word put a horizontal scrollbar under the conversation. Truncating the
 * display is not the same as losing the value: the full string is on the
 * element's `title` and `aria-label`, and a click copies it. Somebody
 * comparing an address against their wallet is comparing the tail, which is
 * why both ends are kept.
 *
 * NOTHING MUTATES THE MESSAGE. The truncation is presentational. The stored
 * string is untouched, and speech is produced on the server from that same
 * string through its own rules — so what is read aloud and what is shown
 * can differ without either being a rewrite of the other.
 *
 * A MESSAGE THAT IS ONLY WORDS COSTS NOTHING. The overwhelming majority are,
 * and they still render as a single text node.
 */

import React from 'react';

import { hasRichContent, parseChatBlocks, parseChatMessage } from './chatRichText';
import type { ChatBlock, ChatSegment, IdentifierSegment } from './chatRichText';

/** Shared by both link cases, so a fallback link matches a real one. */
const linkStyle: React.CSSProperties = {
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  wordBreak: 'break-word',
};

const figureStyle: React.CSSProperties = {
  display: 'block',
  margin: '4px 0',
};

const imageStyle: React.CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  // Bounded so one tall picture cannot push the conversation off screen.
  maxHeight: 220,
  width: 'auto',
  borderRadius: 8,
  objectFit: 'contain',
};

const captionStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 2,
  fontSize: 11,
  opacity: 0.75,
};

/**
 * The identifier chip.
 *
 * `overflowWrap: anywhere` as well as truncation, belt and braces: the
 * truncated form is short, but a renderer that is handed an unexpectedly
 * long value must still not be able to widen its container. The whole point
 * of this component is that the chat panel cannot scroll sideways.
 */
const identifierStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '0.92em',
  padding: '0 4px',
  borderRadius: 4,
  background: 'rgba(148,163,184,0.16)',
  border: '1px solid rgba(148,163,184,0.22)',
  cursor: 'pointer',
  overflowWrap: 'anywhere',
  maxWidth: '100%',
};

const codeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '0.92em',
  padding: '0 3px',
  borderRadius: 3,
  background: 'rgba(148,163,184,0.14)',
  overflowWrap: 'anywhere',
};

const amountStyle: React.CSSProperties = {
  fontWeight: 700,
  color: '#a5b4fc',
  whiteSpace: 'nowrap',
};

const metricStyle: React.CSSProperties = {
  color: '#cbd5e1',
  whiteSpace: 'nowrap',
};

const networkStyle: React.CSSProperties = {
  fontSize: '0.88em',
  textTransform: 'lowercase',
  padding: '0 5px',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.3)',
  color: '#94a3b8',
  whiteSpace: 'nowrap',
};

const statusStyle = (positive: boolean): React.CSSProperties => ({
  fontWeight: 700,
  color: positive ? '#6ee7b7' : '#fca5a5',
});

/** What an identifier is called, for the tooltip and for screen readers. */
const ID_LABEL: Readonly<Record<IdentifierSegment['idKind'], string>> = Object.freeze({
  contract: 'Contract address',
  account: 'Stellar account',
  muxed: 'Stellar account',
  hash: 'Transaction hash',
});

/**
 * An image with a link to fall back to.
 *
 * Stateful because the failure is only discoverable by trying: nothing
 * about the URL says whether it resolves, so the swap happens on the
 * browser's error event.
 */
function ChatImage({ href, alt }: { href: string; alt: string }) {
  const [failed, setFailed] = React.useState(false);

  if (failed) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
        {alt || href}
      </a>
    );
  }

  return (
    <span style={figureStyle}>
      <a href={href} target="_blank" rel="noopener noreferrer">
        <img
          src={href}
          // Empty alt on a decorative image would hide it from a screen
          // reader; here the picture *is* the answer, so it always has one.
          alt={alt || 'Image shared in this conversation'}
          style={imageStyle}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      </a>
      {alt ? <span style={captionStyle}>{alt}</span> : null}
    </span>
  );
}

/**
 * A Stellar address, contract or hash.
 *
 * Copying is best-effort. The clipboard API needs a secure context and a
 * permission, and neither is guaranteed; a refusal leaves the value exactly
 * where it was, still readable in the tooltip. Nothing about the message
 * changes either way, so there is no state worth reconciling — the brief
 * "Copied" acknowledgement is the whole feedback.
 */
function ChatIdentifier({ segment }: { segment: IdentifierSegment }) {
  const [copied, setCopied] = React.useState(false);
  const label = ID_LABEL[segment.idKind];

  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1200);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = React.useCallback(() => {
    void (async () => {
      try {
        await navigator.clipboard?.writeText(segment.value);
        setCopied(true);
      } catch {
        // No clipboard, or permission refused. The full value is still on
        // the title and the aria-label, which is the fallback.
      }
    })();
  }, [segment.value]);

  return (
    <span
      role="button"
      tabIndex={0}
      data-testid="chat-identifier"
      // The complete value, in three places a person or a tool can reach it.
      data-value={segment.value}
      title={`${label}: ${segment.value}`}
      aria-label={`${label} ${segment.value}`}
      style={identifierStyle}
      onClick={copy}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copy(); }
      }}
    >
      {copied ? 'Copied' : segment.display}
    </span>
  );
}

function renderSegment(segment: ChatSegment, key: number): React.ReactNode {
  switch (segment.kind) {
    case 'image':
      return <ChatImage key={key} href={segment.href} alt={segment.alt} />;
    case 'link':
      return (
        <a
          key={key}
          href={segment.href}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          {segment.label}
        </a>
      );
    case 'identifier':
      return <ChatIdentifier key={key} segment={segment} />;
    case 'code':
      return <code key={key} style={codeStyle}>{segment.text}</code>;
    case 'amount':
      return <span key={key} style={amountStyle}>{segment.text}</span>;
    case 'metric':
      return <span key={key} style={metricStyle}>{segment.text}</span>;
    case 'network':
      return <span key={key} style={networkStyle}>{segment.text}</span>;
    case 'status':
      return <span key={key} style={statusStyle(segment.positive)}>{segment.text}</span>;
    default:
      // A fragment rather than a span: this is the common case, and it
      // should add no element to the tree.
      return <React.Fragment key={key}>{segment.text}</React.Fragment>;
  }
}

const paragraphStyle: React.CSSProperties = {
  margin: 0,
  // The rule that makes horizontal overflow impossible for prose. A long
  // unbroken run breaks mid-token rather than widening the card.
  overflowWrap: 'anywhere',
  minWidth: 0,
};

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: '2px 0',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
};

const listItemStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'baseline',
  overflowWrap: 'anywhere',
  minWidth: 0,
};

/**
 * The bullet, which carries meaning rather than decoration.
 *
 * `overviewText` distinguishes an action available now from one that needs
 * something first, and that distinction was arriving as a `•` or a `–` at
 * the start of a run-on line. Here it is a filled or hollow marker, which is
 * the same information in the form somebody actually scans for.
 */
function bulletFor(available: boolean | null): { glyph: string; color: string } {
  if (available === true) return { glyph: '●', color: '#6ee7b7' };
  if (available === false) return { glyph: '○', color: '#fbbf24' };
  return { glyph: '·', color: '#64748b' };
}

function renderBlock(block: ChatBlock, key: number): React.ReactNode {
  if (block.kind === 'list') {
    return (
      <ul key={key} style={listStyle}>
        {block.items.map((item, i) => {
          const bullet = bulletFor(item.available);
          return (
            <li key={i} style={listItemStyle}>
              <span
                aria-hidden="true"
                style={{ color: bullet.color, flex: '0 0 auto', fontSize: '0.8em' }}
              >
                {bullet.glyph}
              </span>
              <span style={{ minWidth: 0 }}>{item.segments.map(renderSegment)}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  return <p key={key} style={paragraphStyle}>{block.segments.map(renderSegment)}</p>;
}

/**
 * Render a message body.
 *
 * @param text The message as stored. Never mutated, and never re-spoken:
 *   speech runs from the same string on the server, through its own rules.
 */
export default function ChatMessageBody({ text }: { text: string }) {
  const blocks = React.useMemo(() => parseChatBlocks(text), [text]);

  // The cheap path, unchanged: one line, only words. Checked against the
  // inline parse rather than the block parse so that a single plain sentence
  // does not gain a paragraph element it never had.
  const single = blocks.length === 1 && blocks[0].kind === 'paragraph';
  if (single) {
    const segments = parseChatMessage(text);
    if (!hasRichContent(segments)) return <>{text}</>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      {blocks.map(renderBlock)}
    </div>
  );
}
